import { Alert, LoadingPanel } from '@dispatch/ui';
import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { apiRequest, errorMessage } from './api.js';
import { permissionIds, permissionLabels, type PermissionId } from './permissions.js';
import type { BusinessSuperadminTransferResult, BusinessUser } from './types.js';

interface Props {
  businessId: string;
  csrfToken: string;
  supportAccessId?: string | undefined;
  canManageUsers: boolean;
  canDelegate: boolean;
  canTransferSuperadmin: boolean;
  canAdministerBusinessSuperadmin: boolean;
  heldPermissionIds: readonly PermissionId[];
  onSuperadminTransferred: (result: BusinessSuperadminTransferResult) => void;
}

type ConfirmableUserAction = 'disable' | 'remove' | 'reset_password' | 'reset_mfa';

interface PendingUserAction {
  user: BusinessUser;
  action: ConfirmableUserAction;
}

interface UserActionConfirmation {
  heading: string;
  warning: string;
  consequence: string;
  confirmLabel: string;
  change: Record<string, unknown>;
  successMessage: string;
}

function userActionConfirmation({ user, action }: PendingUserAction): UserActionConfirmation {
  switch (action) {
    case 'disable':
      return {
        heading: `Disable ${user.display_name}?`,
        warning: `${user.display_name} will immediately lose access to this business.`,
        consequence:
          'Active sessions for this business and registered devices will be revoked. Existing permission assignments will be retained and restored if the membership is reactivated.',
        confirmLabel: 'Disable user',
        change: { status: 'disabled' },
        successMessage: 'The membership was disabled.',
      };
    case 'remove':
      return {
        heading: `Remove ${user.display_name}?`,
        warning: `${user.display_name} will immediately lose access to this business.`,
        consequence:
          'The business membership will be marked as removed, and active sessions for this business and registered devices will be revoked.',
        confirmLabel: 'Remove user',
        change: { status: 'removed' },
        successMessage: 'The membership was removed.',
      };
    case 'reset_mfa':
      return {
        heading: `Reset MFA for ${user.display_name}?`,
        warning:
          'All authenticator methods, passkeys and unused recovery codes for this account will stop working immediately.',
        consequence:
          'All sessions across the platform will be revoked. The account must sign in with its existing password and complete new MFA enrolment.',
        confirmLabel: 'Reset MFA',
        change: { resetMfa: true },
        successMessage: 'MFA was reset and existing sessions were revoked.',
      };
    case 'reset_password':
      if (user.account_status === 'pending') {
        return {
          heading: `Resend invitation to ${user.display_name}?`,
          warning: 'Any existing unused account setup link will stop working.',
          consequence: `A new account setup email will be sent to ${user.email}.`,
          confirmLabel: 'Resend invitation',
          change: { resetPassword: true },
          successMessage: `A new account setup email was queued for ${user.email}.`,
        };
      }
      return {
        heading: `Reset password for ${user.display_name}?`,
        warning:
          'The current password will stop working immediately, even before the emailed reset link is used.',
        consequence:
          'All sessions and pending sign-in challenges across the platform will be revoked. Existing MFA enrolment will be retained.',
        confirmLabel: 'Reset password',
        change: { resetPassword: true },
        successMessage: `A password reset email was queued for ${user.email}. The current password and existing sessions were invalidated.`,
      };
  }
}

export function UsersPanel({
  businessId,
  csrfToken,
  supportAccessId,
  canManageUsers,
  canDelegate,
  canTransferSuperadmin,
  canAdministerBusinessSuperadmin,
  heldPermissionIds,
  onSuperadminTransferred,
}: Props) {
  const [users, setUsers] = useState<BusinessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string>();
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [newPermissions, setNewPermissions] = useState<PermissionId[]>([]);
  const [editingPermissions, setEditingPermissions] = useState<Record<string, PermissionId[]>>({});
  const [transferTarget, setTransferTarget] = useState<BusinessUser>();
  const [transferComplete, setTransferComplete] = useState<BusinessSuperadminTransferResult>();
  const [pendingUserAction, setPendingUserAction] = useState<PendingUserAction>();

  const delegable = useMemo(
    () => permissionIds.filter((permissionId) => heldPermissionIds.includes(permissionId)),
    [heldPermissionIds],
  );

  async function loadUsers(signal?: AbortSignal) {
    if (!canManageUsers) {
      setLoading(false);
      return;
    }
    try {
      const result = await apiRequest<{ users: BusinessUser[] }>(
        `/api/v1/businesses/${businessId}/users`,
        { supportAccessId, ...(signal ? { signal } : {}) },
      );
      setUsers(result.users);
      setEditingPermissions(
        Object.fromEntries(result.users.map((user) => [user.id, [...user.permission_ids]])),
      );
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') return;
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    void loadUsers(controller.signal);
    return () => controller.abort();
  }, [businessId, supportAccessId, canManageUsers]);

  function togglePermission(
    permissionId: PermissionId,
    selected: readonly PermissionId[],
    setter: (value: PermissionId[]) => void,
  ) {
    setter(
      selected.includes(permissionId)
        ? selected.filter((candidate) => candidate !== permissionId)
        : [...selected, permissionId],
    );
  }

  async function addUser(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyId('new');
    setError(undefined);
    setMessage(undefined);
    try {
      const created = await apiRequest<{ setupRequired: boolean }>(
        `/api/v1/businesses/${businessId}/users`,
        {
          method: 'POST',
          csrfToken,
          supportAccessId,
          body: {
            email,
            displayName,
            permissionIds: canDelegate ? newPermissions : [],
          },
        },
      );
      setEmail('');
      setDisplayName('');
      setNewPermissions([]);
      setMessage(
        created.setupRequired
          ? `The business membership was created and an account setup email was queued for ${email}.`
          : 'The business membership was created for an existing account.',
      );
      await loadUsers();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusyId(undefined);
    }
  }

  async function updateMembership(
    user: BusinessUser,
    change: Record<string, unknown>,
    success: string,
  ): Promise<boolean> {
    setBusyId(user.id);
    setError(undefined);
    setMessage(undefined);
    try {
      await apiRequest(`/api/v1/businesses/${businessId}/users/${user.id}`, {
        method: 'PATCH',
        csrfToken,
        supportAccessId,
        body: change,
      });
      setMessage(success);
      await loadUsers();
      return true;
    } catch (caught) {
      setError(errorMessage(caught));
      return false;
    } finally {
      setBusyId(undefined);
    }
  }

  async function confirmUserAction() {
    if (!pendingUserAction) return;
    const confirmation = userActionConfirmation(pendingUserAction);
    await updateMembership(
      pendingUserAction.user,
      confirmation.change,
      confirmation.successMessage,
    );
    setPendingUserAction(undefined);
  }

  async function transferBusinessSuperadmin() {
    if (!transferTarget) return;

    setBusyId(transferTarget.id);
    setError(undefined);
    setMessage(undefined);

    try {
      const result = await apiRequest<{
        superadminTransfer?: BusinessSuperadminTransferResult;
      }>(`/api/v1/businesses/${businessId}/users/${transferTarget.id}`, {
        method: 'PATCH',
        csrfToken,
        supportAccessId,
        body: { makeBusinessSuperadmin: true },
      });

      if (!result.superadminTransfer) {
        throw new Error('The transfer completed without returning its security confirmation.');
      }

      setTransferTarget(undefined);
      setTransferComplete(result.superadminTransfer);
    } catch (caught) {
      setTransferTarget(undefined);
      setError(errorMessage(caught));
    } finally {
      setBusyId(undefined);
    }
  }

  async function savePermissions(user: BusinessUser) {
    const selected = editingPermissions[user.id] ?? [];
    setBusyId(user.id);
    setError(undefined);
    setMessage(undefined);
    try {
      await apiRequest(`/api/v1/businesses/${businessId}/users/${user.id}/permissions`, {
        method: 'PUT',
        csrfToken,
        supportAccessId,
        body: { permissionIds: selected },
      });
      setMessage(`Permissions were updated for ${user.display_name}.`);
      await loadUsers();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusyId(undefined);
    }
  }

  if (!canManageUsers) {
    return (
      <Alert tone="info">The current membership does not hold the Manage Users permission.</Alert>
    );
  }
  if (loading) return <LoadingPanel message="Loading business users…" />;

  const currentSuperadmin = users.find((user) => user.business_superadmin);
  const pendingUserActionDetails = pendingUserAction
    ? userActionConfirmation(pendingUserAction)
    : undefined;

  return (
    <div>
      {pendingUserAction && pendingUserActionDetails ? (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            padding: '24px',
            background: 'rgba(15, 23, 42, 0.58)',
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-action-confirm-heading"
            className="panel"
            style={{ width: 'min(560px, 100%)', margin: 0 }}
          >
            <h2 id="user-action-confirm-heading">{pendingUserActionDetails.heading}</h2>
            <p>{pendingUserActionDetails.warning}</p>
            <p>{pendingUserActionDetails.consequence}</p>
            <p>
              <strong>Are you sure you want to continue?</strong>
            </p>
            <div className="actions">
              <button
                type="button"
                className="secondary"
                disabled={busyId === pendingUserAction.user.id}
                onClick={() => setPendingUserAction(undefined)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger"
                disabled={busyId === pendingUserAction.user.id}
                onClick={() => void confirmUserAction()}
              >
                {busyId === pendingUserAction.user.id
                  ? 'Processing…'
                  : pendingUserActionDetails.confirmLabel}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {transferTarget ? (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            padding: '24px',
            background: 'rgba(15, 23, 42, 0.58)',
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="superadmin-transfer-confirm-heading"
            className="panel"
            style={{ width: 'min(560px, 100%)', margin: 0 }}
          >
            <h2 id="superadmin-transfer-confirm-heading">Transfer Business Superadmin</h2>
            <p>
              All Business Superadmin privileges will be moved to{' '}
              <strong>{transferTarget.display_name}</strong>.
            </p>
            <p>
              <strong>
                {currentSuperadmin?.display_name ?? 'The current Business Superadmin'}
              </strong>{' '}
              will no longer be Business Superadmin and will have no business permissions.
            </p>
            <p>
              {transferTarget.display_name} must sign in and grant permissions to the former
              Business Superadmin if access is still required.
            </p>
            <p>Both accounts will be signed out and both people will receive an email.</p>
            <div className="actions">
              <button
                type="button"
                className="secondary"
                disabled={busyId === transferTarget.id}
                onClick={() => setTransferTarget(undefined)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger"
                disabled={busyId === transferTarget.id}
                onClick={() => void transferBusinessSuperadmin()}
              >
                {busyId === transferTarget.id ? 'Transferring…' : 'Transfer Business Superadmin'}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {transferComplete ? (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            padding: '24px',
            background: 'rgba(15, 23, 42, 0.58)',
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="superadmin-transfer-complete-heading"
            className="panel"
            style={{ width: 'min(560px, 100%)', margin: 0 }}
          >
            <h2 id="superadmin-transfer-complete-heading">Business Superadmin transferred</h2>
            <p>
              All Business Superadmin privileges have been moved to{' '}
              <strong>{transferComplete.newSuperadminName}</strong>.
            </p>
            <p>
              <strong>{transferComplete.previousSuperadminName}</strong> now has no business
              permissions.
            </p>
            <p>
              {transferComplete.newSuperadminName} must sign in and grant permissions if the former
              Business Superadmin still requires access.
            </p>
            <p>Both accounts have been signed out and notification emails have been queued.</p>
            <div className="actions">
              <button
                type="button"
                onClick={() => {
                  const completed = transferComplete;
                  setTransferComplete(undefined);
                  onSuperadminTransferred(completed);
                }}
              >
                {transferComplete.currentSessionRevoked ? 'Return to sign in' : 'Continue'}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {error ? <Alert title="User administration failed">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}

      <section className="panel" aria-labelledby="add-user-heading">
        <h2 id="add-user-heading">Add business user</h2>
        <form className="form-grid" onSubmit={(event) => void addUser(event)}>
          <div className="form-grid form-grid--two">
            <div className="form-field">
              <label htmlFor="new-user-name">Display name</label>
              <input
                id="new-user-name"
                required
                maxLength={160}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="new-user-email">Email address</label>
              <input
                id="new-user-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
          </div>
          {canDelegate ? (
            <fieldset>
              <legend>Initial permissions</legend>
              <div className="permission-grid">
                {delegable.map((permissionId) => (
                  <label key={permissionId}>
                    <input
                      type="checkbox"
                      checked={newPermissions.includes(permissionId)}
                      onChange={() =>
                        togglePermission(permissionId, newPermissions, setNewPermissions)
                      }
                    />
                    <span>{permissionLabels[permissionId]}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : (
            <Alert tone="info">
              The user can be added, but permissions cannot be assigned without Delegate
              Permissions.
            </Alert>
          )}
          <div className="actions">
            <button type="submit" disabled={busyId === 'new'}>
              {busyId === 'new' ? 'Adding…' : 'Add user'}
            </button>
          </div>
        </form>
      </section>

      <section className="panel" aria-labelledby="business-users-heading">
        <h2 id="business-users-heading">Business users</h2>
        {users.length === 0 ? <p>No memberships were returned.</p> : null}
        <div className="card-grid">
          {users.map((user) => {
            const selected = editingPermissions[user.id] ?? [];
            const userBusy = busyId === user.id;
            const canAdministerUser = !user.business_superadmin || canAdministerBusinessSuperadmin;
            return (
              <article className="card" key={user.id}>
                <h3>{user.display_name}</h3>
                <p>
                  <a href={`mailto:${user.email}`}>{user.email}</a>
                </p>
                <p>
                  <span
                    className={`badge ${
                      user.status === 'active'
                        ? 'badge--success'
                        : user.status === 'disabled'
                          ? 'badge--warning'
                          : 'badge--danger'
                    }`}
                  >
                    {user.status}
                  </span>{' '}
                  {user.account_status !== 'active' ? (
                    <span className="badge badge--warning">Account {user.account_status}</span>
                  ) : null}{' '}
                  {user.business_superadmin ? (
                    <span className="badge">Business Superadmin</span>
                  ) : null}
                </p>

                {!user.business_superadmin && canDelegate && user.status === 'active' ? (
                  <fieldset>
                    <legend>Permissions</legend>
                    <div className="permission-grid">
                      {delegable.map((permissionId) => (
                        <label key={permissionId}>
                          <input
                            type="checkbox"
                            checked={selected.includes(permissionId)}
                            onChange={() =>
                              togglePermission(permissionId, selected, (next) =>
                                setEditingPermissions({
                                  ...editingPermissions,
                                  [user.id]: next,
                                }),
                              )
                            }
                          />
                          <span>{permissionLabels[permissionId]}</span>
                        </label>
                      ))}
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="secondary"
                        disabled={userBusy}
                        onClick={() => void savePermissions(user)}
                      >
                        Save permissions
                      </button>
                    </div>
                  </fieldset>
                ) : user.business_superadmin ? (
                  <p className="help-text">All canonical permissions are implicit.</p>
                ) : null}

                {canAdministerUser ? (
                  <div className="actions">
                    {user.status === 'active' && !user.business_superadmin ? (
                      <button
                        type="button"
                        className="secondary"
                        disabled={userBusy}
                        onClick={() => setPendingUserAction({ user, action: 'disable' })}
                      >
                        Disable
                      </button>
                    ) : null}
                    {user.status !== 'active' ? (
                      <button
                        type="button"
                        className="secondary"
                        disabled={userBusy}
                        onClick={() =>
                          void updateMembership(
                            user,
                            { status: 'active' },
                            'The membership was activated.',
                          )
                        }
                      >
                        Activate
                      </button>
                    ) : null}
                    {user.status !== 'removed' && !user.business_superadmin ? (
                      <button
                        type="button"
                        className="danger"
                        disabled={userBusy}
                        onClick={() => setPendingUserAction({ user, action: 'remove' })}
                      >
                        Remove
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="secondary"
                      disabled={userBusy}
                      onClick={() => setPendingUserAction({ user, action: 'reset_password' })}
                    >
                      {user.account_status === 'pending' ? 'Resend invitation' : 'Reset password'}
                    </button>
                    {user.account_status === 'active' ? (
                      <button
                        type="button"
                        className="secondary"
                        disabled={userBusy}
                        onClick={() => setPendingUserAction({ user, action: 'reset_mfa' })}
                      >
                        Reset MFA
                      </button>
                    ) : null}
                    {canTransferSuperadmin &&
                    !user.business_superadmin &&
                    user.status === 'active' ? (
                      <button
                        type="button"
                        className="secondary"
                        disabled={userBusy}
                        onClick={() => setTransferTarget(user)}
                      >
                        Make Business Superadmin
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <p className="help-text">
                    This protected Business Superadmin account can only be administered by the
                    Business Superadmin or Platform Support.
                  </p>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
